import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { ShieldCheck, Copy, GitMerge, Archive } from "lucide-react";
import MergeCompareModal from "@/components/MergeCompareModal";
import { UnitoInfo } from "@/components/UnitoBanner";

const LINK = { notizia: (id) => `/notizie/${id}`, immobile: (id) => `/immobili/${id}` };
const fmt = (s) => { try { return new Date(s).toLocaleDateString("it-IT"); } catch { return ""; } };

function Doppioni({ list, onUnisci }) {
  if (!list) return <div className="skeleton h-20 rounded-xl" />;
  if (list.length === 0) return <p data-testid="guardiana-no-doppioni" className="text-sm text-[var(--muted)]">Nessun contatto doppio.</p>;
  return (
    <div className="space-y-2">
      {list.map((g) => (
        <div key={g.telefono} data-testid={`gd-doppione-${g.telefono}`} className="flex items-center justify-between gap-3 flex-wrap p-3.5 rounded-xl border border-amber-200 bg-amber-50/40">
          <div className="min-w-0">
            <div className="text-sm font-semibold flex items-center gap-1.5"><Copy size={13} /> {g.telefono}</div>
            <div className="text-xs text-[var(--muted)]">{g.schede.map((s) => `${s.nome} (${s.tipo})`).join(" · ")}</div>
          </div>
          <button data-testid={`gd-unisci-${g.telefono}`} onClick={() => onUnisci(g)} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#111111] text-white text-xs font-semibold hover:bg-black"><GitMerge size={13} /> Unisci</button>
        </div>
      ))}
    </div>
  );
}

function Uniti({ list, reload }) {
  const nav = useNavigate();
  if (!list) return <div className="skeleton h-20 rounded-xl" />;
  if (list.length === 0) return <p data-testid="guardiana-no-uniti" className="text-sm text-[var(--muted)]">Nessun contatto unito finora.</p>;
  return (
    <div className="space-y-2">
      {list.map((u) => (
        <div key={u.tipo + u.id} data-testid={`gd-unito-${u.id}`} className="p-3.5 rounded-xl border border-[var(--border)] bg-[#F7F7F8]">
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            {LINK[u.tipo]
              ? <button data-testid={`gd-unito-open-${u.id}`} onClick={() => nav(LINK[u.tipo](u.id))} className="text-sm font-semibold hover:underline">{u.nome}</button>
              : <span className="text-sm font-semibold">{u.nome}</span>}
            <span className="text-[10px] uppercase font-bold text-[var(--muted)]">{u.tipo}</span>
            <span className="text-[11px] text-[var(--muted)] ml-auto">{[u.telefono, u.email].filter(Boolean).join(" · ")} {u.unita_at ? `· unito il ${fmt(u.unita_at)}` : ""}{u.unita_da ? ` da ${u.unita_da}` : ""}</span>
          </div>
          <UnitoInfo tipo={u.tipo} id={u.id} unitaIn={u.unita_in} unitaInNome={u.unita_in_nome} onRestored={reload} />
        </div>
      ))}
    </div>
  );
}

export default function GuardianaDati() {
  const [dop, setDop] = useState(null);
  const [uniti, setUniti] = useState(null);
  const [group, setGroup] = useState(null);
  const load = () => {
    api.get("/dashboard/guardiana?all=1").then((r) => setDop(r.data.doppioni || [])).catch(() => setDop([]));
    api.get("/clienti/uniti").then((r) => setUniti(r.data)).catch(() => setUniti([]));
  };
  useEffect(() => { load(); }, []);
  return (
    <div className="fade-up" data-testid="guardiana-dati-page">
      <div className="mb-6">
        <h1 className="font-display text-3xl lg:text-4xl font-extrabold tracking-tight flex items-center gap-3"><ShieldCheck className="text-[#FC82ED]" size={30} /> Guardiana dati</h1>
        <p className="text-sm text-[var(--muted)] mt-1">Contatti doppi (stesso telefono) da unire senza perdere dati.</p>
      </div>
      <div className="card-immo p-5 mb-6">
        <h2 className="font-display font-bold text-base mb-3 flex items-center gap-2"><Copy size={16} className="text-[#FC82ED]" /> Contatti doppi {dop?.length ? <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">{dop.length}</span> : null}</h2>
        <Doppioni list={dop} onUnisci={setGroup} />
      </div>
      <div className="card-immo p-5">
        <h2 className="font-display font-bold text-base mb-3 flex items-center gap-2"><Archive size={16} className="text-[#FC82ED]" /> Contatti uniti (archiviati)</h2>
        <Uniti list={uniti} reload={load} />
      </div>
      {group && <MergeCompareModal group={group} onClose={() => setGroup(null)} onDone={() => { setGroup(null); load(); }} />}
    </div>
  );
}
